import React, { useEffect, useState } from 'react'
import {
  Form,
  Row,
  Input,
  Button,
  Col,
  Select,
  Popover,
  Space,
  Tag,
  InputNumber,
  Modal,
  Radio,
} from 'antd'
import Styles from './externallink.module.scss'
import { ExclamationCircleOutlined, SwapOutlined } from '@ant-design/icons'
import { CustomerConfirm } from '../../components/ModalUser'
import { PlatformTypeEnum, SearchDataValue } from '../../enum/index'
import Uploadfiles from '../uploadfiles'
import { fileFile } from '../uploadfiles/hooks'
import { formatStr, formatterEditPrice, formatterPrice } from '@/tools/util'
import { normalizeJdProductLink } from '@/tools/jdProductLink'
import PriceItem from './PriceItem'
import { bigCalc } from '@xhgj/xh-jsutils'
import TableAntd from '../../components/TableAntd'
import { Collist } from '../../components/TableAntd/hook'
// 平台类型
const PLATFORM_TYPE_LIST = [
  { value: PlatformTypeEnum.JD, label: '京东' },
  { value: PlatformTypeEnum.TM, label: '天猫' },
  { value: PlatformTypeEnum.SN, label: '苏宁' },
  { value: PlatformTypeEnum.ZKH, label: '震坤行' },
  { value: PlatformTypeEnum.ZCY, label: '政采云' },
  { value: PlatformTypeEnum.DL, label: '得力' },
  { value: PlatformTypeEnum.JS, label: '集什' },
  { value: PlatformTypeEnum.XFS, label: '鑫方盛' },
  { value: PlatformTypeEnum.ELSE, label: '其他' },
]
const linkPatterns = {
  [PlatformTypeEnum.JD]: ['jd.com'], // 京东
  [PlatformTypeEnum.TM]: ['tmall.com', 'taobao.com'], // 天猫
  [PlatformTypeEnum.SN]: ['suning.com'], // 苏宁
  [PlatformTypeEnum.ZKH]: ['zkh.com'], // 震坤行
  [PlatformTypeEnum.ZCY]: ['zcygov.cn'], // 政采云
  [PlatformTypeEnum.DL]: ['b2b.nbdeli.com'], // 得力
  [PlatformTypeEnum.JS]: ['jslink.com'], // 集什
  [PlatformTypeEnum.XFS]: ['xfs.com'], // 鑫方盛
  [PlatformTypeEnum.ELSE]: [], // 其他
}

// 店铺类型
const shopTypeList = [
  { value: 'SELF', label: '自营' },
  { value: 'THIRD_PARTY', label: '第三方' },
  { value: 'BRAND_FLAGSHIP', label: '品牌旗舰店' },
  { value: 'NON_BRAND_FLAGSHIP', label: '非品牌旗舰店' },
]

// 外部链接匹配关系
const linkTypeList = [
  { value: '0', label: '匹配' },
  { value: '1', label: '类似' },
  { value: '2', label: '采集' },
]

type linkTypeKey = (typeof linkTypeList)[number]['value']

export interface ExternalLinkType {
  externalLink: string
  linkType: linkTypeKey
  storeType?: (typeof shopTypeList)[number]['value']
  /** 仅用于物料提交前的页面编辑，不提交后端 */
  inputExternalPrice?: string
  /** 后端持久化的咸亨外链价格 */
  externalPrice?: string
  externalNum?: string
  internalNum?: string
  platformType: PlatformTypeEnum
  id: string
  type: number
  externalFile: fileFile[]
  isLowest?: SearchDataValue
  commentCount?: number
}

export const toExternalLinkPayload = (list: ExternalLinkType[]) =>
  list.map(({ inputExternalPrice, ...item }) => item)

const FormItemStyle: React.CSSProperties = {
  marginLeft: '6px',
}

interface Props {
  form: any
  externalLinkList: ExternalLinkType[]
  onLinkChange: (newList: ExternalLinkType[]) => void
}

// 外部链接
const ExternalLink: React.FC<Props> = (props) => {
  const { externalLinkList, onLinkChange, form } = props
  const [visible, setVisible] = useState<boolean>(false)
  const [linkForm] = Form.useForm()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [openType, setOpenType] = useState('')
  const [isShowTable, setIsShowTable] = useState(true)
  const [uploadFilesList, setUploadFilesList] = useState<fileFile[]>([])
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [cacheEditData, setCacheEditData] = useState({}) as any
  const [currentPrice, setCurrentPrice] = useState('')
  const inputExternalPrice = Form.useWatch('inputExternalPrice', linkForm)
  const externalNum = Form.useWatch('externalNum', linkForm)
  const internalNum = Form.useWatch('internalNum', linkForm)

  useEffect(() => {
    if (
      inputExternalPrice === undefined ||
      inputExternalPrice === null ||
      inputExternalPrice === ''
    ) {
      // 编辑已有外链时原始外链价格不回显，但需保留后端返回的咸亨外链价格。
      if (openType === 'add') {
        linkForm.setFieldValue('externalPrice', undefined)
      }
      return
    }
    const inputPriceValue = Number(inputExternalPrice)
    const externalNumValue = Number(externalNum)
    const internalNumValue = Number(internalNum)
    if (
      Number.isFinite(inputPriceValue) &&
      inputPriceValue >= 0 &&
      Number.isFinite(externalNumValue) &&
      externalNumValue > 0 &&
      Number.isFinite(internalNumValue) &&
      internalNumValue > 0
    ) {
      const calculatedPrice = bigCalc
        .createBig(inputPriceValue)
        .times(externalNumValue)
        .div(internalNumValue)
        .toFixed(2)
      linkForm.setFieldValue('externalPrice', calculatedPrice)
      return
    }
    linkForm.setFieldValue('externalPrice', undefined)
  }, [inputExternalPrice, externalNum, internalNum, linkForm, openType])

  const updateExternalLinkList = (newList: ExternalLinkType[]) => {
    onLinkChange(newList)
  }

  const resetAll = () => {
    setOpenType('')
    setVisible(false)
    setCurrentIndex(0)
    setIsShowTable(true)
    setUploadFilesList([])
    setConfirmLoading(false)
    linkForm.resetFields()
  }

  // 新增打开弹框
  const handleAddLink = () => {
    setOpenType('add')
    setVisible(true)
  }

  // 编辑打开弹框
  const handleEditLink = (record: ExternalLinkType, index: number) => {
    setIsShowTable(record.type === 1)
    setUploadFilesList(record.externalFile)
    // 存储当前行的下标
    setCurrentIndex(index)
    setOpenType('edit')
    setCurrentPrice(
      record.type === 1
        ? record?.inputExternalPrice || ''
        : record?.externalPrice || '',
    )
    setVisible(true)
    setCacheEditData(record)
    linkForm.setFieldsValue(record)
  }

  // 新增||编辑确认
  const onConfirmModal = async () => {
    const linkFormData = await linkForm.validateFields()
    if (linkFormData.type === 1) {
      // 类型为外部链接
      saveExternalLink(linkFormData)
    } else {
      const setNewList = { ...linkFormData }
      if (openType === 'add') {
        updateExternalLinkList(externalLinkList.concat(linkFormData))
        resetAll()
      } else if (openType === 'edit') {
        const editList = externalLinkList.map((item, index) => {
          if (index === currentIndex) {
            return setNewList
          }
          return item
        })
        updateExternalLinkList(editList)
        resetAll()
      }
    }
  }

  const saveExternalLink = (data: ExternalLinkType) => {
    setConfirmLoading(true)
    const setNewList = {
      ...data,
      // 保存时截断，不在输入过程中改写用户正在敲的内容
      externalLink: normalizeJdProductLink(data.externalLink),
      isLowest: data.isLowest ?? SearchDataValue.NO,
    }
    if (openType === 'add') {
      updateExternalLinkList([...externalLinkList, setNewList])
      resetAll()
    } else if (openType === 'edit') {
      const editList = externalLinkList.map((item, index) => {
        if (index === currentIndex) {
          return setNewList
        }
        return item
      })
      updateExternalLinkList(editList)
      resetAll()
    }
  }

  // 删除外链
  const handleDelLink = (index: number) => {
    CustomerConfirm({
      title: '提示',
      content: '您正在删除外部链接，是否确认？',
      onOk: () => {
        const updatedList = [...externalLinkList]
        updatedList.splice(index, 1)
        updateExternalLinkList(updatedList)
      },
    })
  }

  // 关闭弹框
  const onCancelModal = () => {
    resetAll()
  }

  // 外部链接校验
  const validatorExtrnaLink = (rule: any, value: string) => {
    // 检查内容不为空
    if (!value || !value.trim()) {
      return Promise.reject('请填写外部链接地址')
    }

    const urlPattern = /^(https?|http?|ftp):\/\/[^\s/$.?#].[^\s]*$/i
    if (!urlPattern.test(value.trim())) {
      return Promise.reject('暂不支持文本字符，请规范填写链接地址')
    }

    const platformType: PlatformTypeEnum =
      linkForm.getFieldValue('platformType')
    if (platformType) {
      const validDomains = linkPatterns[platformType]
      const isUrl =
        validDomains.length > 0
          ? validDomains.some((item) => {
              return value.includes(item)
            })
          : true

      // 检查链接地址前缀
      if (!isUrl) {
        return Promise.reject('请根据平台类型进行填写')
      }
    }
    // 检查只允许上传一条链接地址
    if (value.trim().split('\n').length > 1) {
      return Promise.reject('暂不支持多条地址链接，请检查后重试')
    }
    return Promise.resolve()
  }

  // 修改平台类型校验链接是否匹配
  const changePlatformType = async (e: any) => {
    linkForm.setFieldsValue({ platformType: e })
    if (linkForm.getFieldValue('externalLink')) {
      await linkForm.validateFields(['externalLink'])
    }
  }

  const onValuesChange = (changedValues: any) => {
    if (changedValues?.type) {
      setIsShowTable(changedValues?.type === 1)
    }
  }

  const onChangeRadio = (e: any) => {
    linkForm.setFieldsValue({
      type: e.target.value,
      linkType: '',
      platformType: '',
      externalPrice: '',
      inputExternalPrice: '',
      storeType: '',
      externalNum: '',
      internalNum: '',
      externalLink: '',
      externalFile: '',
    })
    setUploadFilesList([])
    if (e.target.value === 2) {
      setIsShowTable(false)
    }
    if (openType === 'edit' && e.target.value === cacheEditData.type) {
      linkForm.setFieldsValue(cacheEditData)
      setUploadFilesList(cacheEditData.externalFile)
    }
  }

  const handleUploadfilesChange = (e: fileFile[]) => {
    setUploadFilesList(e)
    linkForm.setFieldsValue({
      externalFile: e,
    })
  }

  // 输入价格类表单失去焦点，格式化
  const onBlurPrice = async (field: string) => {
    const value = linkForm.getFieldValue(field)
    linkForm.setFieldsValue({
      [field]: formatterEditPrice(value),
    })
    await linkForm.validateFields([field])
  }

  const validatorExternalPrice = (rule: any, value: string) => {
    const data = externalLinkList.filter((item) => item.type === 2)
    // 检查内容不为空
    if (!value) {
      return Promise.reject('请输入价格')
    }
    const reg = /^(?!0(\.0{1,2})?$)([1-9]\d{0,6}(\.\d{1,2})?|0\.\d{1,2})$/
    if (!reg.test(value)) {
      return Promise.reject('大于0保留两位小数，小于9999999.99')
    }
    if (openType === 'add') {
      if (data?.some((item) => Number(item.externalPrice) === Number(value))) {
        return Promise.reject('价格不可重复')
      } else {
        return Promise.resolve()
      }
    } else {
      if (
        data?.some(
          (item) =>
            Number(item.externalPrice) === Number(value) &&
            Number(item.externalPrice) !== Number(currentPrice),
        )
      ) {
        return Promise.reject('价格不可重复')
      } else {
        return Promise.resolve()
      }
    }
  }

  const externalLinkColumns: Collist[] = [
    {
      key: 'linkType',
      title: '链接类型',
      width: 90,
      render: (value: linkTypeKey) => (
        <Tag>
          {linkTypeList.find((item) => item.value === value)?.label ?? '-'}
        </Tag>
      ),
    },
    {
      key: 'externalLink',
      title: '外部链接/价格佐证',
      width: 260,
      render: (value: string, record: ExternalLinkType) =>
        record.type === 1 ? (
          <a
            className={Styles['link']}
            href={value}
            target="_blank"
            rel="noreferrer"
            title={value}
            onClick={(event) => event.stopPropagation()}
            style={{ color: '#1677ff' }}
          >
            {value}
          </a>
        ) : (
          <Uploadfiles
            NoUploadButton={true}
            dataSource={record.externalFile}
            hiddenDownload={true}
            disabled={true}
            type="priceSupportingEvidence"
          />
        ),
    },
    {
      key: 'platformType',
      title: '平台类型',
      width: 100,
      render: (value: PlatformTypeEnum, record: ExternalLinkType) =>
        record.type === 1
          ? (PLATFORM_TYPE_LIST.find((item) => item.value === value)?.label ??
            '-')
          : '-',
    },
    {
      key: 'storeType',
      title: '店铺类型',
      width: 120,
      render: (
        value: ExternalLinkType['storeType'],
        record: ExternalLinkType,
      ) =>
        record.type === 1
          ? (shopTypeList.find((item) => item.value === value)?.label ?? '-')
          : '-',
    },
    {
      key: 'externalNum',
      title: '计价单位转换',
      width: 190,
      render: (_value: string, record: ExternalLinkType) =>
        record.type === 1 ? (
          <Space size={8}>
            {Number(record.externalNum ?? 0).toFixed(4)}
            <SwapOutlined />
            {Number(record.internalNum ?? 0).toFixed(4)}
          </Space>
        ) : (
          '-'
        ),
    },
    {
      key: 'externalPrice',
      title: '咸亨外链价格',
      width: 130,
      render: (value: string) => formatterPrice(value),
    },
    {
      key: 'commentCount',
      title: '评论/销量',
      width: 100,
      render: (value: number, record: ExternalLinkType) =>
        record.type === 1 ? (value ?? '-') : '-',
    },
    {
      key: 'operation',
      title: '操作',
      width: 120,
      fixed: 'right',
      render: (
        _value: unknown,
        record: ExternalLinkType & { tableIndex: number },
      ) => (
        <Space size={0}>
          <Button
            type="link"
            onClick={() => handleEditLink(record, record.tableIndex)}
          >
            编辑
          </Button>
          <Button type="link" onClick={() => handleDelLink(record.tableIndex)}>
            删除
          </Button>
        </Space>
      ),
    },
  ]

  const externalLinkTableData = externalLinkList.map((item, index) => ({
    ...item,
    tableIndex: index,
    tableRowKey: item.id || `${item.type}-${index}`,
  }))

  return (
    <div>
      {externalLinkTableData.length > 0 && (
        <TableAntd
          loading={false}
          dataSource={externalLinkTableData}
          listColumn={externalLinkColumns}
          rowKey="tableRowKey"
          scrollCustom={{ x: 1110, y: 240 }}
          scrollStyle={{ height: 'auto', margin: '0 10px' }}
        />
      )}
      {false && (
        <Row
          style={{
            marginLeft: '10px',
            overflowY: 'auto',
            maxHeight: '300px',
            overflowX: 'hidden',
          }}
        >
          {externalLinkList?.length > 0 &&
            externalLinkList.map((item, index) => {
              return (
                <div style={{ marginTop: '10px', width: '100%' }} key={index}>
                  <Row
                    gutter={8}
                    style={{ height: '30px', lineHeight: '30px' }}
                  >
                    <Col lg={1} span={2}>
                      <Tag>
                        {
                          linkTypeList.find(
                            (child) => child.value === item.linkType,
                          )?.label
                        }
                      </Tag>
                    </Col>

                    {item.type === 1 ? (
                      <>
                        外部链接：
                        <Col span={4}>
                          <span
                            className={Styles['link']}
                            onClick={() => {
                              window.open(`${item.externalLink}`)
                            }}
                          >
                            <span style={{ color: 'blue', cursor: 'pointer' }}>
                              {item.externalLink}
                            </span>
                          </span>
                        </Col>
                      </>
                    ) : (
                      <>
                        价格佐证：
                        <Col span={16}>
                          <Uploadfiles
                            NoUploadButton={true}
                            dataSource={item.externalFile}
                            hiddenDownload={true}
                            disabled={true}
                            type="priceSupportingEvidence"
                          />
                        </Col>
                      </>
                    )}

                    {item.type === 1 && (
                      <>
                        <Col span={3}>
                          <span>
                            平台类型：
                            {
                              PLATFORM_TYPE_LIST.find(
                                (child) => child.value === item.platformType,
                              )?.label
                            }
                          </span>
                        </Col>
                        <Col span={3}>
                          <span>
                            店铺类型：
                            {
                              shopTypeList.find(
                                (child) => child.value === item.storeType,
                              )?.label
                            }
                          </span>
                        </Col>
                        <Col span={6}>
                          <div style={{ display: 'flex' }}>
                            计价单位转换：
                            <Space size={8}>
                              {Number(item.externalNum ?? 0).toFixed(4)}
                              <SwapOutlined />
                              {Number(item.internalNum ?? 0).toFixed(4)}
                            </Space>
                          </div>
                        </Col>
                      </>
                    )}

                    <Col span={3}>
                      <span>
                        {item.type === 1 ? '咸亨外链价格：' : '价格：'}
                        {formatterPrice(item.externalPrice)}
                      </span>
                    </Col>
                    {item.type === 1 && (
                      <Col span={2}>
                        <span>评论/销量：{item.commentCount ?? '-'}</span>
                      </Col>
                    )}
                    <Col span={2}>
                      <Button
                        type="link"
                        onClick={() => handleEditLink(item, index)}
                      >
                        编辑
                      </Button>
                      <Button
                        type="link"
                        onClick={() => {
                          handleDelLink(index)
                        }}
                      >
                        删除
                      </Button>
                    </Col>
                  </Row>
                </div>
              )
            })}
        </Row>
      )}

      <div style={{ padding: '12px 12px 24px 12px' }}>
        <Button
          type="dashed"
          style={{ width: '300px', height: '30px' }}
          onClick={handleAddLink}
        >
          +点击添加
        </Button>
      </div>

      <Modal
        title="外部链接/价格佐证"
        width={1040}
        open={visible}
        onOk={onConfirmModal}
        onCancel={onCancelModal}
        confirmLoading={confirmLoading}
        maskClosable={false}
      >
        <Form
          form={linkForm}
          onValuesChange={onValuesChange}
          initialValues={{ type: 1 }}
        >
          <Row>
            <Col>
              <Form.Item name="type">
                <Radio.Group onChange={(e) => onChangeRadio(e)}>
                  <Radio value={1}>外部链接</Radio>
                  <Radio value={2}>价格佐证</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Item
                label="链接类型"
                name="linkType"
                rules={[{ required: true, message: '请选择链接类型' }]}
              >
                <Select
                  style={{ width: 100 }}
                  options={linkTypeList}
                  getPopupContainer={() => document.body}
                  placeholder="请选择"
                />
              </Form.Item>
            </Col>

            {isShowTable ? (
              <>
                <Col>
                  <Form.Item
                    label="平台类型"
                    name="platformType"
                    rules={[{ required: true, message: '请选择平台类型' }]}
                    style={FormItemStyle}
                  >
                    <Select
                      style={{ width: 100 }}
                      options={PLATFORM_TYPE_LIST}
                      getPopupContainer={() => document.body}
                      onChange={changePlatformType}
                      placeholder="请选择"
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item
                    label="店铺类型"
                    name="storeType"
                    rules={[{ required: true, message: '请选择店铺类型' }]}
                    style={FormItemStyle}
                  >
                    <Select
                      style={{ width: 100 }}
                      options={shopTypeList}
                      getPopupContainer={() => document.body}
                      placeholder="请选择"
                    />
                  </Form.Item>
                </Col>
                <Form.Item
                  label={
                    <>
                      <Popover
                        title="提示"
                        content="例如外链单位为支、咸亨单位为盒，若 12支=1盒，则外链销售单位数量填写 12，咸亨销售单位数量填写 1。咸亨外链价格=外链价格×外链销售单位数量÷咸亨销售单位数量。"
                        getPopupContainer={() => document.body}
                        overlayStyle={{ maxWidth: '328px' }}
                      >
                        <ExclamationCircleOutlined />
                      </Popover>
                      计价单位转换
                    </>
                  }
                  style={FormItemStyle}
                  required={true}
                >
                  <Input.Group compact>
                    <Form.Item
                      style={FormItemStyle}
                      name="externalNum"
                      rules={[{ required: true, message: '请输入外链数量' }]}
                    >
                      <InputNumber
                        placeholder="外链数量"
                        precision={4}
                        max={99999999.9999}
                        min={0.0001}
                        style={{ marginRight: 0, width: '120px' }}
                      />
                    </Form.Item>
                    <span
                      style={{
                        width: '30px',
                        padding: '0 10px',
                      }}
                    >
                      <SwapOutlined />
                    </span>
                    <Form.Item
                      style={FormItemStyle}
                      name="internalNum"
                      rules={[{ required: true, message: '请输入咸亨数量' }]}
                    >
                      <InputNumber
                        style={{ width: '120px' }}
                        placeholder="咸亨数量"
                        precision={4}
                        max={99999999.9999}
                        min={0.0001}
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
                <Form.Item
                  name="inputExternalPrice"
                  label="外链价格"
                  style={FormItemStyle}
                >
                  <PriceItem form={linkForm} outerForm={form} />
                </Form.Item>
                <Form.Item
                  name="externalPrice"
                  label="咸亨外链价格"
                  style={FormItemStyle}
                  rules={[{ required: true, message: '请完善换算数量' }]}
                >
                  <InputNumber disabled precision={2} style={{ width: 150 }} />
                </Form.Item>
                <Form.Item
                  name="commentCount"
                  label="评论/销量"
                  style={FormItemStyle}
                  rules={[
                    {
                      validator: (_, value) =>
                        value === undefined ||
                        value === null ||
                        value === '' ||
                        (Number.isInteger(Number(value)) && Number(value) >= 0)
                          ? Promise.resolve()
                          : Promise.reject('评论/销量仅支持填写非负整数'),
                    },
                  ]}
                >
                  <InputNumber min={0} precision={0} style={{ width: 150 }} />
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item
                  style={{ marginLeft: 20 }}
                  label="价格"
                  name="externalPrice"
                  required={true}
                  rules={[
                    {
                      validator: validatorExternalPrice,
                    },
                  ]}
                  getValueFromEvent={(e) => {
                    return formatStr(e.target.value, 'NumberDecimal')
                  }}
                >
                  <Input
                    style={{ width: 150 }}
                    onBlur={() => onBlurPrice('externalPrice')}
                  />
                </Form.Item>
              </>
            )}
          </Row>
          <Row style={{ width: '100%' }}>
            <Col span={24}>
              {isShowTable ? (
                <>
                  <Form.Item
                    name="externalLink"
                    label="链接地址"
                    rules={[
                      {
                        required: true,
                        validator: validatorExtrnaLink,
                      },
                    ]}
                  >
                    <Input.TextArea
                      style={{ margin: '0 10px 0px 0' }}
                      maxLength={2000}
                    />
                  </Form.Item>
                </>
              ) : (
                <>
                  <Form.Item
                    label="佐证附件"
                    rules={[
                      {
                        required: true,
                        message: '请上传佐证附件',
                      },
                    ]}
                    name="externalFile"
                  >
                    <Uploadfiles
                      title="选择文件"
                      type="invoice"
                      accept=".jpg,.png,.pdf"
                      maxCount={5}
                      directlyDelete
                      dataSource={uploadFilesList}
                      onChange={handleUploadfilesChange}
                    />
                    <span style={{ color: '#919191', fontSize: '12px' }}>
                      支持jpg、png、pdf
                    </span>
                  </Form.Item>
                </>
              )}
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  )
}

export default ExternalLink
import React, { useEffect, useState } from 'react'
import {
  Form,
  Row,
  Input,
  Button,
  Col,
  Select,
  Popover,
  Space,
  Tag,
  InputNumber,
  Modal,
  Radio,
} from 'antd'
import Styles from './externallink.module.scss'
import { ExclamationCircleOutlined, SwapOutlined } from '@ant-design/icons'
import { CustomerConfirm } from '../../components/ModalUser'
import { PlatformTypeEnum, SearchDataValue } from '../../enum/index'
import Uploadfiles from '../uploadfiles'
import { fileFile } from '../uploadfiles/hooks'
import { formatStr, formatterEditPrice, formatterPrice } from '@/tools/util'
import { normalizeJdProductLink } from '@/tools/jdProductLink'
import PriceItem from './PriceItem'
import { bigCalc } from '@xhgj/xh-jsutils'
import TableAntd from '../../components/TableAntd'
import { Collist } from '../../components/TableAntd/hook'
// 平台类型
const PLATFORM_TYPE_LIST = [
  { value: PlatformTypeEnum.JD, label: '京东' },
  { value: PlatformTypeEnum.TM, label: '天猫' },
  { value: PlatformTypeEnum.SN, label: '苏宁' },
  { value: PlatformTypeEnum.ZKH, label: '震坤行' },
  { value: PlatformTypeEnum.ZCY, label: '政采云' },
  { value: PlatformTypeEnum.DL, label: '得力' },
  { value: PlatformTypeEnum.JS, label: '集什' },
  { value: PlatformTypeEnum.XFS, label: '鑫方盛' },
  { value: PlatformTypeEnum.ELSE, label: '其他' },
]
const linkPatterns = {
  [PlatformTypeEnum.JD]: ['jd.com'], // 京东
  [PlatformTypeEnum.TM]: ['tmall.com', 'taobao.com'], // 天猫
  [PlatformTypeEnum.SN]: ['suning.com'], // 苏宁
  [PlatformTypeEnum.ZKH]: ['zkh.com'], // 震坤行
  [PlatformTypeEnum.ZCY]: ['zcygov.cn'], // 政采云
  [PlatformTypeEnum.DL]: ['b2b.nbdeli.com'], // 得力
  [PlatformTypeEnum.JS]: ['jslink.com'], // 集什
  [PlatformTypeEnum.XFS]: ['xfs.com'], // 鑫方盛
  [PlatformTypeEnum.ELSE]: [], // 其他
}

// 店铺类型
const shopTypeList = [
  { value: 'SELF', label: '自营' },
  { value: 'THIRD_PARTY', label: '第三方' },
  { value: 'BRAND_FLAGSHIP', label: '品牌旗舰店' },
  { value: 'NON_BRAND_FLAGSHIP', label: '非品牌旗舰店' },
]

// 外部链接匹配关系
const linkTypeList = [
  { value: '0', label: '匹配' },
  { value: '1', label: '类似' },
  { value: '2', label: '采集' },
]

type linkTypeKey = (typeof linkTypeList)[number]['value']

export interface ExternalLinkType {
  externalLink: string
  linkType: linkTypeKey
  storeType?: (typeof shopTypeList)[number]['value']
  /** 仅用于物料提交前的页面编辑，不提交后端 */
  inputExternalPrice?: string
  /** 后端持久化的咸亨外链价格 */
  externalPrice?: string
  externalNum?: string
  internalNum?: string
  platformType: PlatformTypeEnum
  id: string
  type: number
  externalFile: fileFile[]
  isLowest?: SearchDataValue
  commentCount?: number
}

export const toExternalLinkPayload = (list: ExternalLinkType[]) =>
  list.map(({ inputExternalPrice, ...item }) => item)

const FormItemStyle: React.CSSProperties = {
  marginLeft: '6px',
}

interface Props {
  form: any
  externalLinkList: ExternalLinkType[]
  onLinkChange: (newList: ExternalLinkType[]) => void
}

// 外部链接
const ExternalLink: React.FC<Props> = (props) => {
  const { externalLinkList, onLinkChange, form } = props
  const [visible, setVisible] = useState<boolean>(false)
  const [linkForm] = Form.useForm()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [openType, setOpenType] = useState('')
  const [isShowTable, setIsShowTable] = useState(true)
  const [uploadFilesList, setUploadFilesList] = useState<fileFile[]>([])
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [cacheEditData, setCacheEditData] = useState({}) as any
  const [currentPrice, setCurrentPrice] = useState('')
  const inputExternalPrice = Form.useWatch('inputExternalPrice', linkForm)
  const externalNum = Form.useWatch('externalNum', linkForm)
  const internalNum = Form.useWatch('internalNum', linkForm)

  useEffect(() => {
    if (
      inputExternalPrice === undefined ||
      inputExternalPrice === null ||
      inputExternalPrice === ''
    ) {
      // 编辑已有外链时原始外链价格不回显，但需保留后端返回的咸亨外链价格。
      if (openType === 'add') {
        linkForm.setFieldValue('externalPrice', undefined)
      }
      return
    }
    const inputPriceValue = Number(inputExternalPrice)
    const externalNumValue = Number(externalNum)
    const internalNumValue = Number(internalNum)
    if (
      Number.isFinite(inputPriceValue) &&
      inputPriceValue >= 0 &&
      Number.isFinite(externalNumValue) &&
      externalNumValue > 0 &&
      Number.isFinite(internalNumValue) &&
      internalNumValue > 0
    ) {
      const calculatedPrice = bigCalc
        .createBig(inputPriceValue)
        .times(externalNumValue)
        .div(internalNumValue)
        .toFixed(2)
      linkForm.setFieldValue('externalPrice', calculatedPrice)
      return
    }
    linkForm.setFieldValue('externalPrice', undefined)
  }, [inputExternalPrice, externalNum, internalNum, linkForm, openType])

  const updateExternalLinkList = (newList: ExternalLinkType[]) => {
    onLinkChange(newList)
  }

  const resetAll = () => {
    setOpenType('')
    setVisible(false)
    setCurrentIndex(0)
    setIsShowTable(true)
    setUploadFilesList([])
    setConfirmLoading(false)
    linkForm.resetFields()
  }

  // 新增打开弹框
  const handleAddLink = () => {
    setOpenType('add')
    setVisible(true)
  }

  // 编辑打开弹框
  const handleEditLink = (record: ExternalLinkType, index: number) => {
    setIsShowTable(record.type === 1)
    setUploadFilesList(record.externalFile)
    // 存储当前行的下标
    setCurrentIndex(index)
    setOpenType('edit')
    setCurrentPrice(
      record.type === 1
        ? record?.inputExternalPrice || ''
        : record?.externalPrice || '',
    )
    setVisible(true)
    setCacheEditData(record)
    linkForm.setFieldsValue(record)
  }

  // 新增||编辑确认
  const onConfirmModal = async () => {
    const linkFormData = await linkForm.validateFields()
    if (linkFormData.type === 1) {
      // 类型为外部链接
      saveExternalLink(linkFormData)
    } else {
      const setNewList = { ...linkFormData }
      if (openType === 'add') {
        updateExternalLinkList(externalLinkList.concat(linkFormData))
        resetAll()
      } else if (openType === 'edit') {
        const editList = externalLinkList.map((item, index) => {
          if (index === currentIndex) {
            return setNewList
          }
          return item
        })
        updateExternalLinkList(editList)
        resetAll()
      }
    }
  }

  const saveExternalLink = (data: ExternalLinkType) => {
    setConfirmLoading(true)
    const setNewList = {
      ...data,
      // 保存时截断，不在输入过程中改写用户正在敲的内容
      externalLink: normalizeJdProductLink(data.externalLink),
      isLowest: data.isLowest ?? SearchDataValue.NO,
    }
    if (openType === 'add') {
      updateExternalLinkList([...externalLinkList, setNewList])
      resetAll()
    } else if (openType === 'edit') {
      const editList = externalLinkList.map((item, index) => {
        if (index === currentIndex) {
          return setNewList
        }
        return item
      })
      updateExternalLinkList(editList)
      resetAll()
    }
  }

  // 删除外链
  const handleDelLink = (index: number) => {
    CustomerConfirm({
      title: '提示',
      content: '您正在删除外部链接，是否确认？',
      onOk: () => {
        const updatedList = [...externalLinkList]
        updatedList.splice(index, 1)
        updateExternalLinkList(updatedList)
      },
    })
  }

  // 关闭弹框
  const onCancelModal = () => {
    resetAll()
  }

  // 外部链接校验
  const validatorExtrnaLink = (rule: any, value: string) => {
    // 检查内容不为空
    if (!value || !value.trim()) {
      return Promise.reject('请填写外部链接地址')
    }

    const urlPattern = /^(https?|http?|ftp):\/\/[^\s/$.?#].[^\s]*$/i
    if (!urlPattern.test(value.trim())) {
      return Promise.reject('暂不支持文本字符，请规范填写链接地址')
    }

    const platformType: PlatformTypeEnum =
      linkForm.getFieldValue('platformType')
    if (platformType) {
      const validDomains = linkPatterns[platformType]
      const isUrl =
        validDomains.length > 0
          ? validDomains.some((item) => {
              return value.includes(item)
            })
          : true

      // 检查链接地址前缀
      if (!isUrl) {
        return Promise.reject('请根据平台类型进行填写')
      }
    }
    // 检查只允许上传一条链接地址
    if (value.trim().split('\n').length > 1) {
      return Promise.reject('暂不支持多条地址链接，请检查后重试')
    }
    return Promise.resolve()
  }

  // 修改平台类型校验链接是否匹配
  const changePlatformType = async (e: any) => {
    linkForm.setFieldsValue({ platformType: e })
    if (linkForm.getFieldValue('externalLink')) {
      await linkForm.validateFields(['externalLink'])
    }
  }

  const onValuesChange = (changedValues: any) => {
    if (changedValues?.type) {
      setIsShowTable(changedValues?.type === 1)
    }
  }

  const onChangeRadio = (e: any) => {
    linkForm.setFieldsValue({
      type: e.target.value,
      linkType: '',
      platformType: '',
      externalPrice: '',
      inputExternalPrice: '',
      storeType: '',
      externalNum: '',
      internalNum: '',
      externalLink: '',
      externalFile: '',
    })
    setUploadFilesList([])
    if (e.target.value === 2) {
      setIsShowTable(false)
    }
    if (openType === 'edit' && e.target.value === cacheEditData.type) {
      linkForm.setFieldsValue(cacheEditData)
      setUploadFilesList(cacheEditData.externalFile)
    }
  }

  const handleUploadfilesChange = (e: fileFile[]) => {
    setUploadFilesList(e)
    linkForm.setFieldsValue({
      externalFile: e,
    })
  }

  // 输入价格类表单失去焦点，格式化
  const onBlurPrice = async (field: string) => {
    const value = linkForm.getFieldValue(field)
    linkForm.setFieldsValue({
      [field]: formatterEditPrice(value),
    })
    await linkForm.validateFields([field])
  }

  const validatorExternalPrice = (rule: any, value: string) => {
    const data = externalLinkList.filter((item) => item.type === 2)
    // 检查内容不为空
    if (!value) {
      return Promise.reject('请输入价格')
    }
    const reg = /^(?!0(\.0{1,2})?$)([1-9]\d{0,6}(\.\d{1,2})?|0\.\d{1,2})$/
    if (!reg.test(value)) {
      return Promise.reject('大于0保留两位小数，小于9999999.99')
    }
    if (openType === 'add') {
      if (data?.some((item) => Number(item.externalPrice) === Number(value))) {
        return Promise.reject('价格不可重复')
      } else {
        return Promise.resolve()
      }
    } else {
      if (
        data?.some(
          (item) =>
            Number(item.externalPrice) === Number(value) &&
            Number(item.externalPrice) !== Number(currentPrice),
        )
      ) {
        return Promise.reject('价格不可重复')
      } else {
        return Promise.resolve()
      }
    }
  }

  const externalLinkColumns: Collist[] = [
    {
      key: 'linkType',
      title: '链接类型',
      width: 90,
      render: (value: linkTypeKey) => (
        <Tag>
          {linkTypeList.find((item) => item.value === value)?.label ?? '-'}
        </Tag>
      ),
    },
    {
      key: 'externalLink',
      title: '外部链接/价格佐证',
      width: 260,
      render: (value: string, record: ExternalLinkType) =>
        record.type === 1 ? (
          <a
            className={Styles['link']}
            href={value}
            target="_blank"
            rel="noreferrer"
            title={value}
            onClick={(event) => event.stopPropagation()}
            style={{ color: '#1677ff' }}
          >
            {value}
          </a>
        ) : (
          <Uploadfiles
            NoUploadButton={true}
            dataSource={record.externalFile}
            hiddenDownload={true}
            disabled={true}
            type="priceSupportingEvidence"
          />
        ),
    },
    {
      key: 'platformType',
      title: '平台类型',
      width: 100,
      render: (value: PlatformTypeEnum, record: ExternalLinkType) =>
        record.type === 1
          ? (PLATFORM_TYPE_LIST.find((item) => item.value === value)?.label ??
            '-')
          : '-',
    },
    {
      key: 'storeType',
      title: '店铺类型',
      width: 120,
      render: (
        value: ExternalLinkType['storeType'],
        record: ExternalLinkType,
      ) =>
        record.type === 1
          ? (shopTypeList.find((item) => item.value === value)?.label ?? '-')
          : '-',
    },
    {
      key: 'externalNum',
      title: '计价单位转换',
      width: 190,
      render: (_value: string, record: ExternalLinkType) =>
        record.type === 1 ? (
          <Space size={8}>
            {Number(record.externalNum ?? 0).toFixed(4)}
            <SwapOutlined />
            {Number(record.internalNum ?? 0).toFixed(4)}
          </Space>
        ) : (
          '-'
        ),
    },
    {
      key: 'externalPrice',
      title: '咸亨外链价格',
      width: 130,
      render: (value: string) => formatterPrice(value),
    },
    {
      key: 'commentCount',
      title: '评论/销量',
      width: 100,
      render: (value: number, record: ExternalLinkType) =>
        record.type === 1 ? (value ?? '-') : '-',
    },
    {
      key: 'operation',
      title: '操作',
      width: 120,
      fixed: 'right',
      render: (
        _value: unknown,
        record: ExternalLinkType & { tableIndex: number },
      ) => (
        <Space size={0}>
          <Button
            type="link"
            onClick={() => handleEditLink(record, record.tableIndex)}
          >
            编辑
          </Button>
          <Button type="link" onClick={() => handleDelLink(record.tableIndex)}>
            删除
          </Button>
        </Space>
      ),
    },
  ]

  const externalLinkTableData = externalLinkList.map((item, index) => ({
    ...item,
    tableIndex: index,
    tableRowKey: item.id || `${item.type}-${index}`,
  }))

  return (
    <div>
      {externalLinkTableData.length > 0 && (
        <TableAntd
          loading={false}
          dataSource={externalLinkTableData}
          listColumn={externalLinkColumns}
          rowKey="tableRowKey"
          scrollCustom={{ x: 1110, y: 240 }}
          scrollStyle={{ height: 'auto', margin: '0 10px' }}
        />
      )}
      {false && (
        <Row
          style={{
            marginLeft: '10px',
            overflowY: 'auto',
            maxHeight: '300px',
            overflowX: 'hidden',
          }}
        >
          {externalLinkList?.length > 0 &&
            externalLinkList.map((item, index) => {
              return (
                <div style={{ marginTop: '10px', width: '100%' }} key={index}>
                  <Row
                    gutter={8}
                    style={{ height: '30px', lineHeight: '30px' }}
                  >
                    <Col lg={1} span={2}>
                      <Tag>
                        {
                          linkTypeList.find(
                            (child) => child.value === item.linkType,
                          )?.label
                        }
                      </Tag>
                    </Col>

                    {item.type === 1 ? (
                      <>
                        外部链接：
                        <Col span={4}>
                          <span
                            className={Styles['link']}
                            onClick={() => {
                              window.open(`${item.externalLink}`)
                            }}
                          >
                            <span style={{ color: 'blue', cursor: 'pointer' }}>
                              {item.externalLink}
                            </span>
                          </span>
                        </Col>
                      </>
                    ) : (
                      <>
                        价格佐证：
                        <Col span={16}>
                          <Uploadfiles
                            NoUploadButton={true}
                            dataSource={item.externalFile}
                            hiddenDownload={true}
                            disabled={true}
                            type="priceSupportingEvidence"
                          />
                        </Col>
                      </>
                    )}

                    {item.type === 1 && (
                      <>
                        <Col span={3}>
                          <span>
                            平台类型：
                            {
                              PLATFORM_TYPE_LIST.find(
                                (child) => child.value === item.platformType,
                              )?.label
                            }
                          </span>
                        </Col>
                        <Col span={3}>
                          <span>
                            店铺类型：
                            {
                              shopTypeList.find(
                                (child) => child.value === item.storeType,
                              )?.label
                            }
                          </span>
                        </Col>
                        <Col span={6}>
                          <div style={{ display: 'flex' }}>
                            计价单位转换：
                            <Space size={8}>
                              {Number(item.externalNum ?? 0).toFixed(4)}
                              <SwapOutlined />
                              {Number(item.internalNum ?? 0).toFixed(4)}
                            </Space>
                          </div>
                        </Col>
                      </>
                    )}

                    <Col span={3}>
                      <span>
                        {item.type === 1 ? '咸亨外链价格：' : '价格：'}
                        {formatterPrice(item.externalPrice)}
                      </span>
                    </Col>
                    {item.type === 1 && (
                      <Col span={2}>
                        <span>评论/销量：{item.commentCount ?? '-'}</span>
                      </Col>
                    )}
                    <Col span={2}>
                      <Button
                        type="link"
                        onClick={() => handleEditLink(item, index)}
                      >
                        编辑
                      </Button>
                      <Button
                        type="link"
                        onClick={() => {
                          handleDelLink(index)
                        }}
                      >
                        删除
                      </Button>
                    </Col>
                  </Row>
                </div>
              )
            })}
        </Row>
      )}

      <div style={{ padding: '12px 12px 24px 12px' }}>
        <Button
          type="dashed"
          style={{ width: '300px', height: '30px' }}
          onClick={handleAddLink}
        >
          +点击添加
        </Button>
      </div>

      <Modal
        title="外部链接/价格佐证"
        width={1040}
        open={visible}
        onOk={onConfirmModal}
        onCancel={onCancelModal}
        confirmLoading={confirmLoading}
        maskClosable={false}
      >
        <Form
          form={linkForm}
          onValuesChange={onValuesChange}
          initialValues={{ type: 1 }}
        >
          <Row>
            <Col>
              <Form.Item name="type">
                <Radio.Group onChange={(e) => onChangeRadio(e)}>
                  <Radio value={1}>外部链接</Radio>
                  <Radio value={2}>价格佐证</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Item
                label="链接类型"
                name="linkType"
                rules={[{ required: true, message: '请选择链接类型' }]}
              >
                <Select
                  style={{ width: 100 }}
                  options={linkTypeList}
                  getPopupContainer={() => document.body}
                  placeholder="请选择"
                />
              </Form.Item>
            </Col>

            {isShowTable ? (
              <>
                <Col>
                  <Form.Item
                    label="平台类型"
                    name="platformType"
                    rules={[{ required: true, message: '请选择平台类型' }]}
                    style={FormItemStyle}
                  >
                    <Select
                      style={{ width: 100 }}
                      options={PLATFORM_TYPE_LIST}
                      getPopupContainer={() => document.body}
                      onChange={changePlatformType}
                      placeholder="请选择"
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item
                    label="店铺类型"
                    name="storeType"
                    rules={[{ required: true, message: '请选择店铺类型' }]}
                    style={FormItemStyle}
                  >
                    <Select
                      style={{ width: 100 }}
                      options={shopTypeList}
                      getPopupContainer={() => document.body}
                      placeholder="请选择"
                    />
                  </Form.Item>
                </Col>
                <Form.Item
                  label={
                    <>
                      <Popover
                        title="提示"
                        content="例如外链单位为支、咸亨单位为盒，若 12支=1盒，则外链销售单位数量填写 12，咸亨销售单位数量填写 1。咸亨外链价格=外链价格×外链销售单位数量÷咸亨销售单位数量。"
                        getPopupContainer={() => document.body}
                        overlayStyle={{ maxWidth: '328px' }}
                      >
                        <ExclamationCircleOutlined />
                      </Popover>
                      计价单位转换
                    </>
                  }
                  style={FormItemStyle}
                  required={true}
                >
                  <Input.Group compact>
                    <Form.Item
                      style={FormItemStyle}
                      name="externalNum"
                      rules={[{ required: true, message: '请输入外链数量' }]}
                    >
                      <InputNumber
                        placeholder="外链数量"
                        precision={4}
                        max={99999999.9999}
                        min={0.0001}
                        style={{ marginRight: 0, width: '120px' }}
                      />
                    </Form.Item>
                    <span
                      style={{
                        width: '30px',
                        padding: '0 10px',
                      }}
                    >
                      <SwapOutlined />
                    </span>
                    <Form.Item
                      style={FormItemStyle}
                      name="internalNum"
                      rules={[{ required: true, message: '请输入咸亨数量' }]}
                    >
                      <InputNumber
                        style={{ width: '120px' }}
                        placeholder="咸亨数量"
                        precision={4}
                        max={99999999.9999}
                        min={0.0001}
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
                <Form.Item
                  name="inputExternalPrice"
                  label="外链价格"
                  style={FormItemStyle}
                >
                  <PriceItem form={linkForm} outerForm={form} />
                </Form.Item>
                <Form.Item
                  name="externalPrice"
                  label="咸亨外链价格"
                  style={FormItemStyle}
                  rules={[{ required: true, message: '请完善换算数量' }]}
                >
                  <InputNumber disabled precision={2} style={{ width: 150 }} />
                </Form.Item>
                <Form.Item
                  name="commentCount"
                  label="评论/销量"
                  style={FormItemStyle}
                  rules={[
                    {
                      validator: (_, value) =>
                        value === undefined ||
                        value === null ||
                        value === '' ||
                        (Number.isInteger(Number(value)) && Number(value) >= 0)
                          ? Promise.resolve()
                          : Promise.reject('评论/销量仅支持填写非负整数'),
                    },
                  ]}
                >
                  <InputNumber min={0} precision={0} style={{ width: 150 }} />
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item
                  style={{ marginLeft: 20 }}
                  label="价格"
                  name="externalPrice"
                  required={true}
                  rules={[
                    {
                      validator: validatorExternalPrice,
                    },
                  ]}
                  getValueFromEvent={(e) => {
                    return formatStr(e.target.value, 'NumberDecimal')
                  }}
                >
                  <Input
                    style={{ width: 150 }}
                    onBlur={() => onBlurPrice('externalPrice')}
                  />
                </Form.Item>
              </>
            )}
          </Row>
          <Row style={{ width: '100%' }}>
            <Col span={24}>
              {isShowTable ? (
                <>
                  <Form.Item
                    name="externalLink"
                    label="链接地址"
                    rules={[
                      {
                        required: true,
                        validator: validatorExtrnaLink,
                      },
                    ]}
                  >
                    <Input.TextArea
                      style={{ margin: '0 10px 0px 0' }}
                      maxLength={2000}
                    />
                  </Form.Item>
                </>
              ) : (
                <>
                  <Form.Item
                    label="佐证附件"
                    rules={[
                      {
                        required: true,
                        message: '请上传佐证附件',
                      },
                    ]}
                    name="externalFile"
                  >
                    <Uploadfiles
                      title="选择文件"
                      type="invoice"
                      accept=".jpg,.png,.pdf"
                      maxCount={5}
                      directlyDelete
                      dataSource={uploadFilesList}
                      onChange={handleUploadfilesChange}
                    />
                    <span style={{ color: '#919191', fontSize: '12px' }}>
                      支持jpg、png、pdf
                    </span>
                  </Form.Item>
                </>
              )}
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  )
}

export default ExternalLink
